import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import { queryClient } from '@/queryClient';
import { format } from 'date-fns';
import Icon from '../../components/IconWrapper';
import { mdiLoading, mdiPlus, mdiCheck, mdiPaperclip, mdiDownload, mdiTrashCan, mdiPlay, mdiPen, mdiContentSave, mdiCancel, mdiAccountStar } from '@mdi/js';
import VBox from '../../components/layout/VBox';
import HBox from '../../components/layout/HBox';
import Titulo from '../../components/tipografia/Titulo';
import Botao from '../../components/Botao';
import Modal from '../../components/layout/Modal';
import Grid, { GridItem } from '@/components/layout/Grid';
import CampoTexto from '../../components/Formulario/CampoTexto';
import CampoUpload from '../../components/Formulario/CampoUpload';
import CampoData from '../../components/Formulario/CampoData';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import styles from './page.module.css';
import { TicketCompletoDto, TicketStatus, TicketPrioridade, TicketAtividadeDto, TicketAnexoDto } from '@tera/shared/src/dto/TicketDto';
import { Editavel } from '@/components/Editavel';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoMemo from '@/components/Formulario/CampoMemo';
import CampoMemoMencao from '@/components/Formulario/CampoMemoMencao';
import TextoComMencoes from '@/components/tipografia/TextoComMencoes';
import user from '@tera/shared/src/fluxoConfig/ports/domain/user';
import { useAuth } from '@/lib/useAuth';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';

export type TicketDetalheProps = {
    ticketId: number | null;
    onClose: () => void;
    onUpdated?: () => void; // chamado quando algo altera o ticket para permitir refresh externo
    onAssumir: () => void; // chamado quando o usuário clica para assumir o ticket
};

export const TicketDetalhe: React.FC<TicketDetalheProps> = ({ ticketId, onClose, onUpdated, onAssumir }) => {

    const { user } = useAuth();

    const [showAtividadeModal, setShowAtividadeModal] = useState(false);
    const [showAnexoModal, setShowAnexoModal] = useState(false);
    const [novaAtividade, setNovaAtividade] = useState<Partial<TicketAtividadeDto>>({
        descricao: '',
        data_inicio: new Date(),
    });
    const [novoAnexo, setNovoAnexo] = useState<File | null>(null);
    const [atividadeSelecionada, setAtividadeSelecionada] = useState<number | null>(null);
    const [dataConclusao, setDataConclusao] = useState<Date | null>(new Date());
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [novoStatus, setNovoStatus] = useState<TicketStatus | null>(null);
    const [motivoStatus, setMotivoStatus] = useState('');

    const { data: ticket, isLoading, refetch } = useQuery<TicketCompletoDto>(
        ['ticket', ticketId],
        async () => {
            const { data } = await axios.get(`/api/ticket/${ticketId}?completo=true`, { withCredentials: true });
            return data.result;
        },
        { enabled: !!ticketId, refetchOnWindowFocus: false }
    );

    const userCanEdit = ticket &&
        (ticket.criador_id === user.id ||
            (ticket.usuario_responsavel_id === user.id) ||
            ['ADMIN', 'GESTOR'].includes(user.papel)
        );

    useEffect(() => {
        console.log('Ticket atualizado:', ticket);
    }, [ticket]);

    const getStatusColor = (status: TicketStatus) => {
        switch (status) {
            case 'aberto': return 'var(--tc-color-primary)';
            case 'em_progresso': return 'var(--tc-color-primary)';
            case 'finalizado': return 'var(--tc-color-success)';
            case 'cancelado': return 'var(--tc-color-danger)';
            default: return 'var(--tc-color-gray-500)';
        }
    };

    const formatStatus = (status: TicketStatus) => {
        switch (status) {
            case 'aberto': return 'Aberto';
            case 'em_progresso': return 'Em Progresso';
            case 'finalizado': return 'Finalizado';
            case 'cancelado': return 'Cancelado';
            default: return status;
        }
    };

    const formatPrioridade = (priority: TicketPrioridade) => {
        switch (priority) {
            case 'alta': return 'Alta';
            case 'media': return 'Média';
            case 'baixa': return 'Baixa';
            default: return priority;
        }
    };

    const queryUsuarios = useQuery({
        queryKey: 'usuario/box',
        queryFn: async () => {
            const { data } = await axios.get('/api/usuario/box', { withCredentials: true });
            return data.result as { id: number; nome: string }[];
        },
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutos
    })

    const [clienteSearch, setClienteSearch] = useState('');
    const queryClientes = useQuery({
        queryKey: ['cliente/box', clienteSearch],
        queryFn: async () => {
            const { data } = await axios.get('/api/cliente/box?txt=' + clienteSearch, { withCredentials: true });
            return data.result as { id: number; nome: string }[];
        },
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutos
    });
    const clienteOptions = queryClientes.data?.map((c: any) => ({
        id: c.id,
        label: <div>
            <span>{c.nome + (c.sobrenome ? ` ${c.sobrenome}` : '')}</span>
            {c.empresa && <span style={{ fontSize: '0.8rem', fontStyle: 'italic', color: 'var(--tc-color-gray-500' }}>
                {' '} / {c.empresa.nome_fantasia || c.empresa.razao_social}
            </span>}
        </div>
    })) || [];

    const alterarTicketMutation = useMutation(
        async (updates: Partial<TicketCompletoDto>) => {
            const { data } = await axios.put(`/api/ticket/${ticketId}`, updates, { withCredentials: true });
            return data;
        },
        {
            onSuccess: () => {
                toast('Ticket alterado com sucesso', { position: 'top-center', type: 'success' });
                refetch();
                queryClient.invalidateQueries(['ticket', ticketId]);
                onUpdated?.();
            },
            onError: (error: any) => {
                if (error.response?.data?.validationErrors) {
                    const errors: Record<string, string> = {};
                    error.response.data.validationErrors.forEach((err: { path: string; message: string }) => {
                        errors[err.path] = err.message;
                    });
                    setFieldErrors(errors);
                }
                const msg = error?.response?.data?.msg ?? 'Erro ao alterar ticket';
                toast(msg, { position: 'top-center', type: 'error' });
            }
        }
    );

    const alterarStatusMutation = useMutation(
        async ({ status, motivo }: { status: TicketStatus; motivo?: string }) => {
            const payload: any = { status };
            if (motivo) {
                if (status === 'finalizado') payload.descricao_resolucao = motivo;
                if (status === 'cancelado') payload.motivo_cancelamento = motivo;
            }
            const { data } = await axios.put(`/api/ticket/${ticketId}/status`, payload, { withCredentials: true });
            return data;
        },
        {
            onSuccess: () => {
                toast('Status do ticket alterado com sucesso', { position: 'top-center', type: 'success' });
                setShowStatusModal(false);
                setMotivoStatus('');
                setNovoStatus(null);
                refetch();
                queryClient.invalidateQueries(['ticket', ticketId]);
                onUpdated?.();
            },
            onError: (err: any) => {
                const msg = err?.response?.data?.msg ?? 'Erro ao alterar status do ticket';
                toast(msg, { position: 'top-center', type: 'error' });
            }
        }
    );

    const adicionarAtividadeMutation = useMutation(
        async (atividade: Partial<TicketAtividadeDto>) => {
            const { data } = await axios.post(`/api/ticket/${ticketId}/atividade`, atividade, { withCredentials: true });
            return data;
        },
        {
            onSuccess: () => {
                toast('Atividade adicionada com sucesso', { position: 'top-center', type: 'success' });
                setShowAtividadeModal(false);
                setNovaAtividade({ descricao: '', data_inicio: new Date() });
                refetch();
                queryClient.invalidateQueries(['ticket', ticketId]);
                onUpdated?.();
            },
            onError: (error: any) => {
                if (error.response?.data?.validationErrors) {
                    const errors: Record<string, string> = {};
                    error.response.data.validationErrors.forEach((err: { path: string; message: string }) => {
                        errors[err.path] = err.message;
                    });
                    setFieldErrors(errors);
                }
                toast('Erro ao adicionar atividade', { position: 'top-center', type: 'error' });
            }
        }
    );

    const finalizarAtividadeMutation = useMutation(
        async ({ id, data_fim }: { id: number; data_fim: Date }) => {
            const { data } = await axios.put(`/api/ticket/${ticket?.id}/atividade/${id}/finalizar`, { data_fim }, { withCredentials: true });
            return data;
        },
        {
            onSuccess: () => {
                toast('Atividade finalizada com sucesso', { position: 'top-center', type: 'success' });
                setAtividadeSelecionada(null);
                setDataConclusao(new Date());
                refetch();
                queryClient.invalidateQueries(['ticket', ticketId]);
                onUpdated?.();
            },
            onError: (err: any) => {
                const msg = err?.response?.data?.msg ?? 'Erro ao finalizar atividade';
                toast(msg, { position: 'top-center', type: 'error' });
            }
        }
    );

    const uploadAnexoMutation = useMutation(
        async (file: File) => {
            const formData = new FormData();
            formData.append('arquivo', file);
            const { data } = await axios.post(`/api/ticket/${ticketId}/anexo`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
            return data;
        },
        {
            onSuccess: () => {
                toast('Anexo enviado com sucesso', { position: 'top-center', type: 'success' });
                setShowAnexoModal(false);
                setNovoAnexo(null);
                refetch();
                queryClient.invalidateQueries(['ticket', ticketId]);
                onUpdated?.();
            },
            onError: () => {
                toast('Erro ao enviar anexo', { position: 'top-center', type: 'error' });
            }
        }
    );

    const excluirAnexoMutation = useMutation(
        async (anexoId: number) => {
            const { data } = await axios.delete(`/api/ticket/${ticketId}/anexo/${anexoId}`, { withCredentials: true });
            return data;
        },
        {
            onSuccess: () => {
                toast('Anexo excluído com sucesso', { position: 'top-center', type: 'success' });
                refetch();
                queryClient.invalidateQueries(['ticket', ticketId]);
                onUpdated?.();
            },
            onError: () => {
                toast('Erro ao excluir anexo', { position: 'top-center', type: 'error' });
            }
        }
    );

    const handleAlterarTicket = (updates: Partial<TicketCompletoDto>) => {
        alterarTicketMutation.mutate({ id: ticketId ?? undefined, ...updates });
    };

    const handleAdicionarAtividade = () => {
        if (!novaAtividade.descricao) {
            setFieldErrors({ descricao: 'Descrição é obrigatória' });
            return;
        }
        if (!novaAtividade.data_inicio) {
            setFieldErrors({ data_inicio: 'Data de início é obrigatória' });
            return;
        }
        adicionarAtividadeMutation.mutate(novaAtividade);
    };

    const handleFinalizarAtividade = () => {
        if (!atividadeSelecionada || !dataConclusao) return;
        finalizarAtividadeMutation.mutate({ id: atividadeSelecionada, data_fim: dataConclusao });
    };

    const handleEnviarAnexo = () => {
        if (novoAnexo) {
            uploadAnexoMutation.mutate(novoAnexo);
        } else {
            toast('Selecione um arquivo para anexar', { position: 'top-center', type: 'warning' });
        }
    };

    const handleDownloadAnexo = async (anexo: TicketAnexoDto) => {
        try {
            const response = await axios.get(`/api/ticket/${ticketId}/anexo/${anexo.id}/download`, { responseType: 'blob', withCredentials: true });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', anexo.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch {
            toast('Erro ao baixar anexo', { position: 'top-center', type: 'error' });
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const isSLAOverdue = (ticketData: TicketCompletoDto) => {
        if (!ticketData.sla_deadline) return false;
        return new Date(ticketData.sla_deadline) < new Date();
    };

    if (!ticketId) return null;

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <Icon path={mdiLoading} size={2} color="var(--tc-color-primary)" spin />
            </div>
        );
    }

    if (!ticket || ticket == null) {
        return <div>Ticket não encontrado</div>;
    }

    return (
        <div className={styles.containerDetalhes} style={{ overflow: 'auto', paddingRight: 8 }}>
            <VBox gap="16px">
                <HBox>
                    {ticket.status === 'aberto' && (
                        <Botao
                            variant="primary"
                            style={{ marginLeft: 'auto' }}
                            onClick={() => {
                                setNovoStatus('em_progresso');
                                alterarStatusMutation.mutate({ status: 'em_progresso' });
                            }}
                            disabled={alterarStatusMutation.isLoading}
                            icon={mdiPlay}
                        >
                            Iniciar Atendimento
                        </Botao>
                    )}

                    {ticket.status === 'em_progresso' && userCanEdit && (
                        <Botao
                            variant="warning"
                            style={{ marginLeft: 'auto' }}
                            onClick={() => {
                                setShowStatusModal(true);
                            }}
                            disabled={alterarStatusMutation.isLoading}
                            icon={mdiCheck}
                        >
                            Finalizar / Cancelar
                        </Botao>
                    )}

                    {(ticket.status === 'finalizado' || ticket.status === 'cancelado') && userCanEdit && (
                        <Botao
                            variant="primary"
                            style={{ marginLeft: 'auto' }}
                            onClick={() => {
                                alterarStatusMutation.mutate({ status: 'aberto' });
                            }}
                            disabled={alterarStatusMutation.isLoading}
                            icon={mdiPlay}
                        >
                            Reabrir
                        </Botao>
                    )}
                </HBox>

                <Grid>
                    <GridItem sm={12} md={8}>
                        <VBox>
                            <Editavel
                                enabled={userCanEdit}
                                inline={false}
                                value={ticket.titulo}
                                onSave={(novo) => {
                                    return alterarTicketMutation.mutateAsync({ id: ticket.id, titulo: novo });
                                }}
                                validate={(v) => (v.trim() === '' ? 'Título não pode ser vazio' : null)}
                                view={(v) => (
                                    <Titulo>
                                        Ticket #{ticket.id}: {v}
                                    </Titulo>
                                )}
                                editor={({ value, setValue, error, autoFocus }) => (
                                    <CampoTexto
                                        value={value}
                                        onChange={setValue}
                                        error={error || fieldErrors['titulo']}
                                        autoFocus={autoFocus}
                                        instanceId="ticket-titulo"
                                    />
                                )}
                            />
                            <HBox gap="16px" style={{ marginTop: '8px' }}>
                                <div
                                    className={styles.statusBadge}
                                    style={{
                                        backgroundColor: `${getStatusColor(ticket.status)}20`,
                                        color: getStatusColor(ticket.status),
                                        borderColor: getStatusColor(ticket.status),
                                    }}
                                >
                                    {formatStatus(ticket.status)}
                                </div>
                                <Editavel
                                    enabled={userCanEdit}
                                    inline={true}
                                    value={ticket.prioridade as string | null}
                                    onSave={(novo) => {
                                        return alterarTicketMutation.mutateAsync({ id: ticket.id, prioridade: novo as TicketPrioridade });
                                    }}
                                    validate={(v) => (['baixa', 'media', 'alta'].includes(v as string) ? null : 'Prioridade inválida')}
                                    view={(v) => (
                                        <div
                                            className={styles.priorityBadge}
                                            style={{
                                                backgroundColor:
                                                    ticket.prioridade === 'alta'
                                                        ? 'rgba(var(--tc-color-error-rgb), 0.1)'
                                                        : ticket.prioridade === 'media'
                                                            ? 'rgba(var(--tc-color-warning-rgb), 0.1)'
                                                            : 'rgba(var(--tc-color-success-rgb), 0.1)',
                                                color:
                                                    ticket.prioridade === 'alta'
                                                        ? 'var(--tc-color-danger)'
                                                        : ticket.prioridade === 'media'
                                                            ? 'var(--tc-color-warning)'
                                                            : 'var(--tc-color-success)',
                                            }}
                                        >
                                            {formatPrioridade(ticket.prioridade)}
                                        </div>
                                    )}
                                    editor={({ value, setValue, error, autoFocus }) => (
                                        <CampoSeletor
                                            noSearch
                                            value={value}
                                            onChange={setValue}
                                            options={[
                                                { label: 'Baixa', id: 'baixa' },
                                                { label: 'Média', id: 'media' },
                                                { label: 'Alta', id: 'alta' },
                                            ]}
                                            error={error || fieldErrors['prioridade']}
                                            autoFocus={autoFocus}
                                            instanceId="ticket-prioridade"
                                        />
                                    )}
                                />

                                {ticket.sla_deadline && (
                                    <Editavel
                                        enabled={userCanEdit}
                                        inline={true}
                                        value={ticket.sla_deadline ?? undefined}
                                        onSave={(novo) => {
                                            const novaData = novo ? new Date(novo) : null;
                                            return alterarTicketMutation.mutateAsync({ id: ticket.id, sla_deadline: novaData ?? undefined });
                                        }}
                                        validate={(v) => (isNaN(new Date(v).getTime()) ? 'Data inválida' : null)}
                                        view={(v) => (
                                            <div
                                                className={`${styles.slaBadge} ${isSLAOverdue(ticket) && ticket.status !== 'finalizado' && ticket.status !== 'cancelado'
                                                    ? styles.slaOverdue
                                                    : ''
                                                    }`}
                                            >
                                                SLA: {format(v, 'dd/MM/yyyy HH:mm')}
                                                {isSLAOverdue(ticket) && ticket.status !== 'finalizado' && ticket.status !== 'cancelado' && ' (Vencido)'}
                                            </div>
                                        )}
                                        editor={({ value, setValue, error, autoFocus }) => (
                                            <CampoData
                                                value={value ? format(value, 'yyyy-MM-dd HH:mm') : ''}
                                                onChange={(v) => setValue(new Date(v))}
                                                error={error || fieldErrors['sla_deadline']}
                                                autoFocus={autoFocus}
                                                instanceId="ticket-sla"
                                                type='datetime-local'
                                            />
                                        )}
                                    />
                                )}
                            </HBox>
                        </VBox>
                    </GridItem>
                    <GridItem sm={12} md={4}>
                        <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
                            {ticket.status !== 'finalizado' && ticket.status !== 'cancelado' && (
                                <>
                                    <Botao variant="outline-primary" icon={mdiPlus} onClick={() => setShowAtividadeModal(true)}>
                                        Nova Atividade
                                    </Botao>
                                    <Botao variant="outline-secondary" icon={mdiPaperclip} onClick={() => setShowAnexoModal(true)}>
                                        Anexar
                                    </Botao>
                                </>
                            )}
                        </HBox>
                    </GridItem>
                </Grid>

                <div className={styles.ticketDetails}>
                    <Grid>
                        <GridItem sm={12} md={4}>
                            <Editavel
                                enabled={userCanEdit}
                                inline={false}
                                value={ticket.cliente}
                                onSave={(novo) => {
                                    return alterarTicketMutation.mutateAsync({ id: ticket.id, cliente_id: novo?.id ?? undefined });
                                }}
                                view={(v) => (
                                    <VBox gap="8px">
                                        <div style={{ fontWeight: 'bold' }}>Cliente:</div>
                                        <div>{v?.nome || 'N/A'}</div>
                                    </VBox>
                                )}
                                editor={({ value, setValue, error, autoFocus }) => (
                                    <CampoSeletor
                                        options={clienteOptions}
                                        value={value?.id?.toString()}
                                        onChange={(v) => setValue(queryClientes.data?.find(c => c.id.toString() === v) || undefined)}
                                        error={error || fieldErrors['cliente']}
                                        autoFocus={autoFocus}
                                        instanceId="ticket-cliente"
                                        onChangeSearch={setClienteSearch}
                                        hasRemoteSearch
                                    />
                                )}
                            />
                        </GridItem>
                        <GridItem sm={12} md={4}>
                            <VBox gap="8px">
                                <div style={{ fontWeight: 'bold' }}>Criado por:</div>
                                <div>{ticket.criador?.nome || 'N/A'} em {ticket.data_criacao ? format(new Date(ticket.data_criacao), 'dd/MM/yyyy HH:mm') : null}</div>
                            </VBox>
                        </GridItem>
                        <GridItem sm={12} md={4}>
                            <VBox gap="8px">
                                <div style={{ fontWeight: 'bold' }}>Responsável:</div>
                                <div>{ticket.usuario_responsavel?.nome || <HBox>
                                    <span style={{ color: 'var(--tc-color-warning)' }}>Sem responsável atribuído.</span>
                                    <Botao variant="none-primary" icon={mdiAccountStar} onClick={() => onAssumir()}>Assumir agora</Botao>
                                </HBox>
                                }</div>
                            </VBox>
                        </GridItem>

                        <GridItem sm={12}>
                            <VBox gap="8px" style={{ textAlign: 'center', alignItems: 'center', marginTop: '16px' }}>
                                <div style={{ fontWeight: 'bold' }}>Descrição:</div>
                                <div>{ticket.descricao}</div>
                            </VBox>
                        </GridItem>

                        {ticket.status === 'finalizado' && ticket.descricao_resolucao && (
                            <GridItem sm={12}>
                                <VBox gap="8px" style={{ marginTop: '16px', textAlign: 'center', alignItems: 'center' }}>
                                    <h2 style={{ color: 'var(--tc-color-primary)' }}>Resolução:</h2>
                                    <TextoComMencoes value={ticket.descricao_resolucao} />
                                    {ticket.data_fechamento && <div
                                        style={{ textAlign: 'center', marginTop: '8px', color: 'var(--tc-color-gray-500)', fontSize: '0.8rem' }}
                                    >
                                        Finalizado em: {format(ticket.data_fechamento, 'dd/MM/yyyy HH:mm')}
                                    </div>}
                                </VBox>
                            </GridItem>
                        )}
                        {ticket.status === 'cancelado' && ticket.motivo_cancelamento && (
                            <GridItem sm={12}>
                                <VBox gap="8px" style={{ marginTop: '16px', textAlign: 'center', alignItems: 'center' }}>
                                    <h2 style={{ color: 'var(--tc-color-primary)' }}>Motivo do Cancelamento:</h2>
                                    <TextoComMencoes value={ticket.motivo_cancelamento} />
                                    {ticket.data_fechamento && <div
                                        style={{ textAlign: 'center', marginTop: '8px', color: 'var(--tc-color-gray-500)', fontSize: '0.8rem' }}
                                    >
                                        Cancelado em: {format(ticket.data_fechamento, 'dd/MM/yyyy HH:mm')}
                                    </div>}
                                </VBox>
                            </GridItem>
                        )}
                    </Grid>
                </div>

                <div className={styles.ticketActivities}>
                    <Titulo>Atividades</Titulo>
                    {ticket.atividades && ticket.atividades.length > 0 ? (
                        ticket.atividades.map((atividade) => {
                            if (atividade.tipo === 'historico') return (
                                <div key={atividade.id} className={styles.activityHistoryItem}>
                                    <div className={styles.activityHeader}>
                                        <div style={{ fontWeight: '500' }}>{atividade.usuario_responsavel?.nome || 'Usuário'}</div>
                                        <div className={styles.activityDescription}>
                                            <TextoComMencoes value={atividade.descricao} />
                                        </div>
                                        <div className={styles.activityDate}>{format(new Date(atividade.data_inicio), 'dd/MM/yyyy HH:mm')}</div>
                                    </div>
                                </div>
                            );
                            return (
                                <div key={atividade.id} className={styles.activityItem}>
                                    <div className={styles.activityHeader}>
                                        <div style={{ fontWeight: '500' }}>{atividade.usuario_responsavel?.nome || 'Usuário'}</div>
                                        <div className={styles.activityDate}>
                                            {format(new Date(atividade.data_inicio), 'dd/MM/yyyy HH:mm')}
                                            {atividade.data_fim && ` - ${format(new Date(atividade.data_fim), 'dd/MM/yyyy HH:mm')}`}
                                        </div>
                                    </div>
                                    <div style={{ whiteSpace: 'pre-wrap' }}>
                                        <TextoComMencoes value={atividade.descricao} />
                                    </div>
                                    {!atividade.data_fim && ticket.status !== 'finalizado' && ticket.status !== 'cancelado' && atividade.usuario_responsavel_id == user.id && (
                                        <div style={{ marginTop: '8px', textAlign: 'right' }}>
                                            <Botao variant="secondary" size="small" icon={mdiCheck} onClick={() => { setAtividadeSelecionada(atividade.id); setDataConclusao(new Date()); }}>
                                                Finalizar
                                            </Botao>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div style={{ marginTop: '8px', color: 'var(--tc-color-gray-500)' }}>Nenhuma atividade registrada</div>
                    )}
                </div>

                <div>
                    <Titulo>Anexos</Titulo>
                    {ticket.anexos && ticket.anexos.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                            {ticket.anexos.map((anexo) => (
                                <div key={anexo.id} style={{ padding: '16px', borderRadius: '8px', backgroundColor: 'var(--tc-color-background-subtle)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div style={{ fontWeight: '500', wordBreak: 'break-word' }}>{anexo.filename}</div>
                                    <div style={{ fontSize: '0.85rem', color: 'var(--tc-color-gray-500)' }}>
                                        {formatFileSize(anexo.tamanho)} • {format(new Date(anexo.data_upload), 'dd/MM/yyyy HH:mm')}<br />
                                        Enviado por: {anexo.uploader?.nome || 'Usuário'}
                                    </div>
                                    <HBox gap="8px" style={{ justifyContent: 'flex-end', marginTop: '8px' }}>
                                        <Botao variant="primary" size="small" icon={mdiDownload} onClick={() => handleDownloadAnexo(anexo)}>
                                            Download
                                        </Botao>
                                        {ticket.status !== 'finalizado' && ticket.status !== 'cancelado' && (
                                            <BotaoPerigoso buttonProps={{ size: 'small' }} variant="danger" icon={mdiTrashCan} confirmation={<div><p>Tem certeza que deseja excluir este anexo?</p><p>Esta ação não pode ser desfeita.</p></div>} onClick={() => excluirAnexoMutation.mutate(anexo.id)}>
                                                Excluir
                                            </BotaoPerigoso>
                                        )}
                                    </HBox>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ marginTop: '8px', color: 'var(--tc-color-gray-500)' }}>Nenhum anexo adicionado</div>
                    )}
                </div>
            </VBox>

            {/* Modais internos */}
            <Modal size="md" show={showAtividadeModal} title="Nova Atividade" onClose={() => setShowAtividadeModal(false)} onCancel={() => setShowAtividadeModal(false)} onConfirm={handleAdicionarAtividade} confirmProps={{ disabled: adicionarAtividadeMutation.isLoading }}>
                <Grid>
                    <GridItem sm={12}>
                        {queryUsuarios.data && (<CampoMemoMencao
                            instanceId="descricao-atividade"
                            label="Descrição da Atividade"
                            value={novaAtividade.descricao || ''}
                            onChange={(v) => setNovaAtividade({ ...novaAtividade, descricao: v })}
                            error={fieldErrors.descricao}
                            required
                            usuarios={queryUsuarios.data}

                        />)}
                    </GridItem>
                    <GridItem sm={12}>
                        <CampoData
                            instanceId="data-inicio"
                            label="Data de Início"
                            value={format(novaAtividade.data_inicio ?? new Date(), 'yyyy-MM-dd HH:mm')}
                            onChange={(v) => setNovaAtividade({ ...novaAtividade, data_inicio: new Date(v) })}
                            error={fieldErrors.data_inicio}
                            type="datetime-local"
                            required
                        />
                    </GridItem>
                    <GridItem sm={12}>
                        <CampoCheckbox
                            instanceId="publicar-atividade"
                            value={!novaAtividade.interna || false}
                            onChange={(v) => setNovaAtividade({ ...novaAtividade, interna: !v })}
                        >
                            Publicar atividade
                        </CampoCheckbox>

                        {novaAtividade.interna && (
                            <div style={{ fontSize: '0.85rem', color: 'var(--tc-color-gray-500)' }}>
                                A atividade será marcada como interna e não ficará visível para o cliente.
                            </div>
                        )}
                        {!novaAtividade.interna && (
                            <div style={{ fontSize: '0.85rem', color: 'var(--tc-color-gray-500)' }}>
                                A atividade será visível para o cliente.
                            </div>
                        )}
                    </GridItem>
                </Grid>
            </Modal>

            <Modal show={showAnexoModal} title="Adicionar Anexo" onClose={() => setShowAnexoModal(false)} onCancel={() => setShowAnexoModal(false)} onConfirm={handleEnviarAnexo}>
                <CampoUpload instanceId="upload-anexo" label="Selecione um arquivo" onChange={(files) => setNovoAnexo(files[0])} value={novoAnexo ? [novoAnexo] : []} maxFiles={1} accept="*/*" />
            </Modal>

            <Modal show={!!atividadeSelecionada} title="Finalizar Atividade" onClose={() => setAtividadeSelecionada(null)} onCancel={() => setAtividadeSelecionada(null)} onConfirm={handleFinalizarAtividade}>
                <CampoData instanceId="data-conclusao" label="Data de Conclusão" value={format(dataConclusao ? dataConclusao : new Date(), 'yyyy-MM-dd HH:mm')} onChange={(v: string) => setDataConclusao(new Date(v))} type="datetime-local" required />
            </Modal>

            <Modal show={showStatusModal} title="Finalizar ou Cancelar Ticket" onClose={() => { setShowStatusModal(false); setMotivoStatus(''); setNovoStatus(null); }} onCancel={() => { setShowStatusModal(false); setMotivoStatus(''); setNovoStatus(null); }} onConfirm={() => { if (novoStatus && motivoStatus) { alterarStatusMutation.mutate({ status: novoStatus, motivo: motivoStatus }); } }} confirmProps={{ disabled: !novoStatus || !motivoStatus || alterarStatusMutation.isLoading }}>
                <VBox gap="16px">
                    <CampoSeletor
                        noSearch
                        instanceId="seletor-status"
                        label="Novo Status"
                        value={novoStatus || ''}
                        onChange={(v) => setNovoStatus(v as TicketStatus)}
                        options={[
                            { label: 'Finalizado', id: 'finalizado' },
                            { label: 'Cancelado', id: 'cancelado' },
                        ]}
                        placeholder="Selecione o novo status"
                        required
                    />
                    <CampoMemo instanceId="motivo-status" label={novoStatus === 'finalizado' ? 'Motivo da Finalização' : novoStatus === 'cancelado' ? 'Motivo do Cancelamento' : 'Motivo'} value={motivoStatus} onChange={setMotivoStatus} required />
                </VBox>
            </Modal>
        </div>
    );
};
